import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";
import { LivechatSubjectType } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { Conference } from "@scrile/api-provider/dist/projects/webvideo/ConferenceProvider";
import useAuthUser from "hooks/useAuthUser";
import { LOCAL_STORAGE_NAMES } from "const";
import providers from "../../lib/providers";
import { Routes } from "../../router/config";

interface Props {
  conferenceUuid: string;
}

export default function useController({ conferenceUuid }: Props) {
  const history = useHistory();
  const { user } = useAuthUser();
  const [loading, setLoading] = useState(false);
  const [conference, setConference] = useState<Conference | null>(null);
  const [groupThreadId, setGroupThreadId] = useState<string | undefined>();

  const onClose = useCallback(() => {
    if (conference?.groupId) {
      history.push(generatePath(Routes.groupMessages, { groupId: String(conference.groupId) }));
    } else {
      history.push(Routes.messages);
    }
  }, [conference, history]);
  const livechatSubject: Subject<LivechatSubjectType> | null = conference
    ? {
        subjectId: conference.id,
        subjectType: LivechatSubjectType.CONFERENCE,
      }
    : null;

  const ownerId = useMemo(() => conference?.ownerId ?? "0", [conference]);
  const groupId = useMemo(() => conference?.groupId, [conference]);

  const mountedRef = useRef(true);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const getConferenceData = async () => {
    try {
      setLoading(true);

      if (!user) {
        // Guest flow: use guestJoinConference which bypasses auth via internal context
        try {
          const guestResponse = await providers.GroupsProvider.guestJoinConference(
            conferenceUuid,
            localStorage.getItem(LOCAL_STORAGE_NAMES.LIVE_CHAT_NAME) || "Guest"
          );
          if (!mountedRef.current) return;
          setConference({
            id: guestResponse.conference.id,
            ownerId: guestResponse.conference.ownerId,
            uuid: guestResponse.conference.uuid || conferenceUuid,
            groupId: guestResponse.conference.groupId,
            active: guestResponse.conference.active,
          } as Conference);
          // Skip message thread lookup for guests
          return;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("[conference] Guest join failed:", e);
          onClose();
          return;
        }
      }

      // Authenticated user flow
      const response = await providers.ConferenceProvider.search({ uuids: [conferenceUuid] });
      if (!response.result.length) {
        onClose();
        return;
      }
      const conf = response.result[0];
      if (!mountedRef.current) return;
      setConference(conf);

      // Find group message thread for system messages
      if (conf.groupId) {
        try {
          const threads = await providers.MessageThreadsProvider.find({
            data: { subjectType: "group", subjectId: String(conf.groupId), size: 1 },
          });
          if (threads.result?.length > 0 && mountedRef.current) {
            setGroupThreadId(String(threads.result[0].id));
          }
        } catch {
          // Group thread may not exist yet
        }
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { getConferenceData(); }, []);

  return {
    history,
    livechatSubject,
    loading,
    ownerId,
    groupId,
    onClose,
    groupThreadId,
  };
}
